import React from "react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, Button, Grid, Stack, Tooltip, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { formatCountryCode } from "utils/formatters/CountryFormatters";
import type { BillingAccountAddressDto, BillingAccountDetailDto, IntegrationAttemptDto } from "client/api-v2";
import { DateTimeText } from "components/DateTimeText/DateTimeText";
import { DetailField, detailFieldLabelSx } from "components/InfoField/InfoField";
import { EmptyValue, MutedPlaceholder } from "components/MutedPlaceholder";
import { PolicyAuthorized } from "components/authorization/PolicyAuthorized";
import { RbacPolicies } from "components/authorization/RbacPolicies";
import { attentionPurpose } from "../attention";
import { AccountIssueChip } from "../components/AccountIssueChip";
import { IntegrationAttemptStatusChip } from "../components/IntegrationAttemptStatusChip";
import { paymentTermLabel } from "../paymentTerms";

// Imported addresses carry whitespace where a value is absent as readily as they carry null, so a
// part counts as present only once trimmed.
const addressPart = (value?: string | null) => value?.trim() || null;

function addressLines(address: BillingAccountAddressDto): string[] {
  const region = [addressPart(address.state), addressPart(address.postalCode)].filter(Boolean).join(" ");
  const locality = [addressPart(address.city), region].filter(Boolean).join(", ");
  const countryCode = addressPart(address.countryCode);

  return [
    addressPart(address.address1),
    addressPart(address.address2),
    locality,
    countryCode && formatCountryCode(countryCode),
  ].filter((line): line is string => !!line);
}

function Address({ address }: { address: BillingAccountAddressDto | null }) {
  // The generated type says the address is always present, but the API really does send null (see
  // the nullable-$ref note in the admin portal CLAUDE.md), and one can also arrive with every part
  // empty.
  const lines = address ? addressLines(address) : [];

  if (lines.length === 0) {
    return <MutedPlaceholder>No address</MutedPlaceholder>;
  }

  return (
    <Stack>
      {lines.map((line, index) => (
        <span key={`${line}-${index}`}>{line}</span>
      ))}
    </Stack>
  );
}

// The label carries the tooltip, which `DetailField` cannot do — its label is a string — so this
// borrows `detailFieldLabelSx` to line up with the fields around it, as the tax exemption block does.
function Attention({ firstName, lastName, email }: { firstName: string; lastName: string; email: string }) {
  return (
    <Box>
      <Stack direction="row" spacing={0.5} sx={{ alignItems: "center", mb: 0.5 }}>
        <Typography variant="body2" sx={{ color: "text.secondary", ...detailFieldLabelSx }}>
          Attention
        </Typography>
        <Tooltip title={attentionPurpose}>
          <InfoOutlinedIcon fontSize="small" sx={{ color: "text.secondary" }} />
        </Tooltip>
      </Stack>
      <Typography variant="body1" component="div">
        <Stack>
          <span>
            {firstName} {lastName}
          </span>
          <Box component="span" sx={{ fontFamily: "monospace" }}>
            {email}
          </Box>
        </Stack>
      </Typography>
    </Box>
  );
}

function ExemptionField({ label, value }: { label: string; value: string | null }) {
  return (
    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
      <DetailField label={label} value={value ?? <EmptyValue />} />
    </Grid>
  );
}

// A full-width band under the two columns rather than a field inside one. Sharing a column with the
// address stretches these pairs far enough apart to stop reading as one group, and boxing them is what
// distinguishes the heading from the field labels it sits above.
function TaxExemption({ billingAccount }: { billingAccount: BillingAccountDetailDto }) {
  return (
    <Stack spacing={1}>
      <Typography variant="body2" sx={{ color: "text.secondary", ...detailFieldLabelSx }}>
        Tax exemption
      </Typography>
      {billingAccount.isTaxExempt ? (
        <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 2 }}>
          <Grid container spacing={2}>
            <ExemptionField label="Certificate ID" value={billingAccount.taxExemptionCertificateId} />
            <ExemptionField label="Certificate type" value={billingAccount.taxExemptionCertificateType} />
            <ExemptionField label="Issuing jurisdiction" value={billingAccount.taxExemptionJurisdiction} />
            <ExemptionField label="Entity/use code" value={billingAccount.taxExemptionEntityUseCode} />
            <ExemptionField label="Description" value={billingAccount.taxExemptionDescription} />
          </Grid>
        </Box>
      ) : (
        // Five placeholders would read as missing data rather than as an unexceptional account.
        <MutedPlaceholder>Not exempt</MutedPlaceholder>
      )}
    </Stack>
  );
}

function LastPush({ attempt }: { attempt: IntegrationAttemptDto | null | undefined }) {
  return (
    <DetailField
      label="Last push"
      value={
        attempt ? (
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <IntegrationAttemptStatusChip attempt={attempt} />
            <DateTimeText dateTime={DateTime.fromISO(attempt.startedAt)} labelFormat="dd MMM yyyy, hh:mm:ss a" />
          </Stack>
        ) : (
          <MutedPlaceholder>Never pushed</MutedPlaceholder>
        )
      }
    />
  );
}

function ZuoraSync({
  latestIntegrationAttempt,
  onPush,
}: {
  latestIntegrationAttempt: IntegrationAttemptDto | null | undefined;
  onPush: () => void;
}) {
  return (
    <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 2 }}>
      <Stack spacing={2}>
        <Typography variant="body2" sx={{ color: "text.secondary", ...detailFieldLabelSx }}>
          Zuora sync
        </Typography>
        <Typography variant="body2">
          Local edits do not modify Zuora. Data is written through when an order or quote is submitted.
        </Typography>
        <LastPush attempt={latestIntegrationAttempt} />
        <PolicyAuthorized policies={RbacPolicies.BillingAccounts.ReadWrite}>
          <Box>
            <Button id="push-billing-account-to-zuora-button" color="secondary" variant="outlined" onClick={onPush}>
              Push to Zuora
            </Button>
          </Box>
        </PolicyAuthorized>
      </Stack>
    </Box>
  );
}

interface BillingAccountSummaryProps {
  billingAccount: BillingAccountDetailDto;
  onPushToBillingSystem: () => void;
}

export function BillingAccountSummary({ billingAccount, onPushToBillingSystem }: BillingAccountSummaryProps) {
  return (
    <Stack spacing={3}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Stack spacing={2}>
            <DetailField label="Name" value={billingAccount.commonName ?? <EmptyValue />} />
            <DetailField label="CRM account" value={billingAccount.crmCustomerId ?? <EmptyValue />} />
            <DetailField label="Zuora billing account" value={billingAccount.zuoraAccountNumber ?? <EmptyValue />} />
          </Stack>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Stack spacing={2}>
            <Attention
              firstName={billingAccount.attentionFirstName}
              lastName={billingAccount.attentionLastName}
              email={billingAccount.attentionEmail}
            />
            <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start" }}>
              <DetailField label="Address" value={<Address address={billingAccount.address} />} />
              {billingAccount.hasAddressIssue && <AccountIssueChip issue="address" />}
            </Stack>
            <DetailField label="Tax ID" value={billingAccount.taxId ?? <EmptyValue />} />
            <DetailField label="Payment term" value={paymentTermLabel(billingAccount.paymentTerm)} />
          </Stack>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <ZuoraSync
            latestIntegrationAttempt={billingAccount.latestIntegrationAttempt}
            onPush={onPushToBillingSystem}
          />
        </Grid>
      </Grid>
      <TaxExemption billingAccount={billingAccount} />
    </Stack>
  );
}
